import { Injectable } from '@angular/core';
import { TranslateService } from '@client/app/services/translate/translate.service';
import {
    PagePerformanceAuditDetailsService, PagePerformanceAuditHeadingType,
} from '@client/app/modules/page-performance/services/page-performance-audit-details/page-performance-audit-details.service';
import { MonNumberService } from '@monsido/core/locale/services/mon-number.service';

@Injectable({
    providedIn: 'root',
})
export class PerformanceAuditsDetailsParser {
    constructor (
        private readonly translateService: TranslateService,
        private readonly pagePerformanceAuditDetailsService: PagePerformanceAuditDetailsService,
        private readonly monNumberSerivce: MonNumberService,
    ) {}

    private getHeadlines (): string[] {
        return [
            this.translateService.getString('URL'),
            this.translateService.getString('Size'),
            this.translateService.getString('Potential Savings'),
        ];
    }

    private getDiagnosticsHeadlinesEnsureTextRemainsVisibleDuringWebfontLoad (): string[] {
        return [
            this.translateService.getString('URL'),
            this.translateService.getString('Potential Savings'),
        ];
    }

    private getDiagnosticsHeadlinesAvoidsEnormousNetworkPayloads (): string[] {
        return [
            this.translateService.getString('URL'),
            this.translateService.getString('Size'),
        ];
    }

    private getDiagnosticsHeadlinesKeepRequestCountsLowAndTransferSizesSmall (): string[] {
        return [
            this.translateService.getString('Resource Type'),
            this.translateService.getString('Requests'),
            this.translateService.getString('Transfer Size'),
        ];
    }

    private getDiagnosticsHeadlinesMinimizeMainThreadWork (): string[] {
        return [
            this.translateService.getString('Category'),
            this.translateService.getString('Time Spent'),
        ];
    }

    private getDiagnosticsHeadlinesServeStaticAssetsWithAnEfficientCachePolicy (): string[] {
        return [
            this.translateService.getString('URL'),
            this.translateService.getString('Cache TTL'),
            this.translateService.getString('Size'),
        ];
    }

    private getDiagnosticsHeadlinesReduceJavaScriptExecutionTime (): string[] {
        return [
            this.translateService.getString('URL'),
            this.translateService.getString('Total CPU Time'),
            this.translateService.getString('Script Evaluation'),
            this.translateService.getString('Script Parse'),
        ];
    }

    private getDiagnosticsHeadlinesAvoidsAnExcessiveDOMSize (): string[] {
        return [
            this.translateService.getString('Statistic'),
            this.translateService.getString('Element'),
            this.translateService.getString('Value'),
        ];
    }

    private getDiagnosticsHeadlinesThirdPartyUsage (): string[] {
        return [
            this.translateService.getString('Third-Party'),
            this.translateService.getString('Size'),
            this.translateService.getString('Main-Thread Blocking Time'),
        ];
    }

    private getDiagnosticsHeadlinesMinimizeCriticalRequestsDepth (): string[] {
        return [
            this.translateService.getString('URL'),
            this.translateService.getString('Response Time'),
            this.translateService.getString('Size'),
        ];
    }

    parse (data: any[]): string[][] {
        if (!data || data.length === 0) {
            return [];
        }

        const csv: string[][] = [];
        let entry: string[];

        if (data[0].totalBytes &&
            (data[0].wastedMs || data[0].wastedBytes) &&
            typeof data[0].cacheLifetimeMs === 'undefined') {
            csv.push(this.getHeadlines());

            for (const item of data) {
                entry = [];
                entry.push(item.url);
                entry.push(this.pagePerformanceAuditDetailsService.getBytesSnippet(item.totalBytes));
                if (item.wastedMs) {
                    entry.push(this.pagePerformanceAuditDetailsService.getMSSnippet(item.wastedMs));
                } else {
                    entry.push(this.pagePerformanceAuditDetailsService.getBytesSnippet(item.wastedBytes));
                }
                csv.push(entry);
            }
            return csv;
        } else if (!data[0].totalBytes && !data[0].label && !data[0].requestCount &&
                   !data[0].size && !data[0].groupLabel && !data[0].duration &&
                   typeof data[0].total === 'undefined' && typeof data[0].scripting === 'undefined' &&
                   typeof data[0].scriptParseCompile === 'undefined' && typeof data[0].statistic === 'undefined' &&
                   typeof data[0].element === 'undefined' && typeof data[0].value === 'undefined' &&
                   typeof data[0].entity === 'undefined' && typeof data[0].transferSize === 'undefined' &&
                   typeof data[0].request === 'undefined') {
            csv.push(this.getDiagnosticsHeadlinesEnsureTextRemainsVisibleDuringWebfontLoad());

            for (const item of data) {
                entry = [];
                entry.push(item.url);
                if (item.wastedMs) {
                    entry.push(this.pagePerformanceAuditDetailsService.getMSSnippet(item.wastedMs));
                } else {
                    entry.push(this.pagePerformanceAuditDetailsService.getBytesSnippet(item.wastedBytes));
                }
                csv.push(entry);
            }
            return csv;
        } else if (data[0].totalBytes && !data[0].label && !data[0].requestCount &&
                   !data[0].size && !data[0].groupLabel && !data[0].duration &&
                   typeof data[0].cacheLifetimeMs === 'undefined') {
            csv.push(this.getDiagnosticsHeadlinesAvoidsEnormousNetworkPayloads());

            for (const item of data) {
                entry = [];
                entry.push(item.url);
                entry.push(this.pagePerformanceAuditDetailsService.getBytesSnippet(item.totalBytes));
                csv.push(entry);
            }
            return csv;
        } else if (data[0].label && data[0].requestCount && data[0].size) {
            csv.push(this.getDiagnosticsHeadlinesKeepRequestCountsLowAndTransferSizesSmall());

            for (const item of data) {
                entry = [];
                entry.push(item.label);
                entry.push(item.requestCount.toString());
                entry.push(this.pagePerformanceAuditDetailsService.getBytesSnippet(item.size));
                csv.push(entry);
            }
            return csv;
        } else if (data[0].groupLabel && data[0].duration) {
            csv.push(this.getDiagnosticsHeadlinesMinimizeMainThreadWork());

            for (const item of data) {
                entry = [];
                entry.push(item.groupLabel);
                entry.push(this.pagePerformanceAuditDetailsService.getMSSnippet(item.duration));
                csv.push(entry);
            }
            return csv;
        } else if (data[0].url && (data[0].cacheLifetimeMs === 0 || data[0].cacheLifetimeMs) && data[0].totalBytes) {
            csv.push(this.getDiagnosticsHeadlinesServeStaticAssetsWithAnEfficientCachePolicy());

            for (const item of data) {
                entry = [];
                entry.push(item.url);
                entry.push(this.pagePerformanceAuditDetailsService.getMSSnippet(item.cacheLifetimeMs));
                entry.push(this.pagePerformanceAuditDetailsService.getBytesSnippet(item.totalBytes));
                csv.push(entry);
            }
            return csv;
        } else if (data[0].url && data[0].total && data[0].scripting && data[0].scriptParseCompile) {
            csv.push(this.getDiagnosticsHeadlinesReduceJavaScriptExecutionTime());

            for (const item of data) {
                entry = [];
                entry.push(item.url);
                entry.push(this.pagePerformanceAuditDetailsService.getMSSnippet(item.total));
                entry.push(this.pagePerformanceAuditDetailsService.getMSSnippet(item.scripting));
                entry.push(this.pagePerformanceAuditDetailsService.getMSSnippet(item.scriptParseCompile));
                csv.push(entry);
            }
            return csv;
        } else if (data[0].statistic && data[0].value) {
            csv.push(this.getDiagnosticsHeadlinesAvoidsAnExcessiveDOMSize());

            for (const item of data) {
                entry = [];
                entry.push(item.statistic);
                entry.push(
                    item.element.value,
                );
                entry.push(item.value.toString());
                csv.push(entry);
            }
            return csv;
        } else if (data[0].entity && (data[0].blockingTime === 0 || data[0].blockingTime) && data[0].transferSize) {
            csv.push(this.getDiagnosticsHeadlinesThirdPartyUsage());

            for (const item of data) {
                entry = [];
                entry.push(item.entity.text);
                entry.push(this.pagePerformanceAuditDetailsService.getBytesSnippet(item.transferSize));
                entry.push(this.pagePerformanceAuditDetailsService.getMSSnippet(item.blockingTime));
                csv.push(entry);
            }
            return csv;
        } else if (data[0].request) {
            csv.push(this.getDiagnosticsHeadlinesMinimizeCriticalRequestsDepth());

            for (const item of data) {
                entry = [];
                entry.push(item.request.url);
                entry.push(
                    this.monNumberSerivce.transformNumber((item.request.endTime - item.request.startTime) * 1000, '0') + 'ms',
                );
                entry.push(this.monNumberSerivce.transformNumber(item.request.transferSize, '0.00 ib'));
                csv.push(entry);
            }
            return csv;
        }

        return [];
    }
}
